import React, { useState, useEffect } from 'react';
import { Dialog } from '../../shared/custom-ui';
import { V2Button } from '../../shared/custom-ui/v2-components';
import { toast } from 'react-toastify';
import ApiServices from '../../shared/ApiServices/ApiServices';

type MedicalNotesDialogProps = {
    isOpen: boolean;
    mode: 'create' | 'edit';
    patientId: string | null;
    existingNote?: { id: string; text: string } | null;
    onClose: () => void;
    onSuccess: () => void;
};

const MedicalNotesDialog: React.FC<MedicalNotesDialogProps> = ({
    isOpen,
    mode,
    patientId,
    existingNote,
    onClose,
    onSuccess,
}) => {
    const [note, setNote] = useState('');
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        if (mode === 'edit' && existingNote?.text) setNote(existingNote.text);
        else setNote('');
    }, [mode, existingNote]);

    const handleSubmit = async () => {
        if (!note.trim()) return toast.error('Please enter a note.');
        if (!patientId) return toast.error('Patient ID is missing.');

        setLoading(true);
        try {
            if (mode === 'edit') {
                await ApiServices.editNote(patientId, existingNote?.id, note);
                toast.success('Note updated successfully.');
            } else {
                await ApiServices.createNote(patientId, note);
                toast.success('Note created successfully!');
            }

            setNote('');
            onClose();
            onSuccess();
        } catch {
            toast.error('Something went wrong. Please try again.');
        } finally {
            setLoading(false);
        }
    };

    if (!isOpen) return null;

    return (
        <Dialog
            isOpen={isOpen}
            onClose={onClose}
            size="small"
            title={mode === 'edit' ? 'Edit Medical Note' : 'Add Medical Note'}
            showCloseButton
            className="!overflow-visible min-w-[60vh]"
            footer={
                <div className="flex justify-end gap-2">
                    <V2Button variant="danger" onClick={onClose} disabled={loading}>
                        Cancel
                    </V2Button>
                    <V2Button
                        variant="primary"
                        size="small"
                        onClick={handleSubmit}
                        disabled={loading || !note.trim()}
                        loading={loading}
                    >
                        {loading ? 'Saving...' : mode === 'edit' ? 'Update Note' : 'Add Note'}
                    </V2Button>
                </div>
            }
        >
            <div className="relative">
                <textarea
                    className="w-full min-h-[160px] resize-none rounded-[var(--radius-md)] border p-3.5 text-[var(--font-body)]
                     placeholder-[var(--text-muted)] shadow-[inset_0_1px_2px_rgba(0,0,0,0.06)] transition-all duration-[var(--duration-normal)]
                     focus:border-[var(--primary-400)] focus:ring-[3px] focus:ring-[var(--primary-500)]/15"
                    style={{ background: 'var(--bg-surface)', borderColor: 'var(--border-subtle)', color: 'var(--text-primary)' }}
                    placeholder="Write your medical note here..."
                    value={note}
                    onChange={(e) => setNote(e.target.value)}
                    disabled={loading}
                />

                {loading && (
                    <div className="absolute inset-0 flex items-center justify-center" style={{ background: 'var(--bg-card)', opacity: 0.85 }}>
                        <div className="h-6 w-6 animate-spin rounded-full border-2 border-blue-500 border-t-transparent" />
                    </div>
                )}
            </div>
        </Dialog>
    );
};

export default MedicalNotesDialog;
